import { cn } from '@h2hspark/ui';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { useMemberSession } from '@/auth/auth-provider';

import { useOwnProfile } from '../domains/members/api';

import { memberInitials, PROFILE_DESTINATION } from './destinations';

/**
 * **The member's avatar, wherever it is drawn** — the member app's desktop header (`MEM-042`
 * AC-3) and, since 2026-09-21, the marketing site's header (`MEM-043` AC-8).
 *
 * ## Why it is one component
 *
 * It was the member shell's, inline, and TW 44131893 is the whole argument for lifting it out: the
 * avatar is **the member profile**, not the session. The session's `displayName` is the name the
 * identity provider stored at sign-up; Your account edits the profile, so a member who renames
 * themselves there must see it here. Reading the same query Your account reads
 * (`useOwnProfile`) means every save, and every photo upload or removal, reaches the avatar the
 * moment the mutation invalidates or replaces it.
 *
 * A second copy on the marketing header would have been a second answer to *who is this*, and
 * TW 44131893 is a report about exactly that — two sources for one person, drifted apart. So the
 * marketing header renders this, and there is one place where the rule lives.
 *
 * ## It asks for nothing without a session
 *
 * `enabled` is gated on the session because both headers are drawn for signed-out visitors too,
 * and asking for a profile without one would be a 401 on every page load — on the marketing site,
 * on every public page. With no session this renders `null`: the caller decides what stands in its
 * place, which for both headers is the signed-out actions.
 */
export function MemberAvatarLink({
  /** Marked `aria-current="page"` when the avatar's own destination is the screen on show. */
  current = false,
  className,
}: {
  readonly current?: boolean;
  readonly className?: string;
}): React.ReactElement | null {
  const { session } = useMemberSession();
  const signedIn = session !== null;
  const ownProfile = useOwnProfile({ enabled: signedIn });

  if (session === null) return null;

  const profileShared = ownProfile.data?.shared;
  const name =
    profileShared === undefined
      ? undefined
      : `${profileShared.firstName} ${profileShared.lastName}`.trim() || undefined;
  const photoUrl = profileShared?.photoUrl ?? null;

  return (
    <Link
      to={PROFILE_DESTINATION}
      aria-current={current ? 'page' : undefined}
      className={cn(
        'inline-flex h-[38px] w-[38px] flex-none items-center justify-center overflow-hidden rounded-pill bg-spark font-brand text-[13px] font-bold text-white',
        current ? 'ring-2 ring-primary ring-offset-2 ring-offset-card' : '',
        className,
      )}
      aria-label="You"
    >
      {photoUrl === null ? (
        // The profile's name, and the session's only while there is no profile to read — still
        // loading, or an account that stopped before the profile questions.
        memberInitials(name ?? session.displayName)
      ) : (
        // Decorative: the link is named "You", and the photo says nothing more to a screen reader
        // than that.
        <img src={photoUrl} alt="" className="h-full w-full rounded-pill object-cover" />
      )}
    </Link>
  );
}
